'use client';

import React, { useEffect, useState, useTransition } from 'react';
import { Flex4XS } from '@/components/Flex';
import FormikTextInput from '@/components/core/formik/FormikTextInput';
import Typography from '@/components/core/Typography/Typography';
import { Field, Form, Formik } from 'formik';
import { useTranslations } from 'next-intl';
import * as Yup from 'yup';
import { Link, useRouter } from '@/i18n/routing';
import { logIn } from '@/actions/auth/login';
import useAmplitudeContext from '@/hooks/useAmplitudeContext';
import { AMPLITUDE_EVENTS, toAmplitudeUserID } from '@/context/AmplitudeContext';
import { getUser } from '@/actions/account/getUser';
import GoogleAuth, { postLoginRedirect } from '../../_components/GoogleAuth';
import {
    Frame,
    GreyTypography,
    PageWrapper2VerticalSections,
    StyledButton,
    StyledMainFrame,
    StyledSecondaryTitle,
    Title
} from '@/app/[locale]/(welcome)/auth/_styled';
import FormikCheckbox from '@/components/core/formik/FormikCheckbox';
import { MoonOneLogo, NeedHelpBlock } from '@/app/[locale]/(welcome)/_components';
import { Link as TypographyLink } from '@/components/Link';
import { FlexCenter } from '@/components/Helpers';
import { EMAIL_REGEX } from '@/utils/regex';
import {
    FieldsBlock, ForgotPasswordButton, GoogleBlock, Line, OrBlock,
    StyledFlexSpaceBetween,
    StyledIcon
} from '@/app/[locale]/(welcome)/auth/(with_banner)/login/_styled';
import AlertWithIcon from '@/components/core/Alert/AlertWithIcon';
import capitalize from '@/utils/capitalize';
import * as amplitude from '@amplitude/analytics-browser';
import { formatTitle } from '@/utils/formatTitle';
import { HideBelow, HideAbove, device } from '@/components/Media';
import styled from 'styled-components';

const SignUpWrapper = styled(Flex4XS)`
    @media screen and ${device.laptopS} {
        flex-direction: column;
        align-items: center;
    }
`;

const validationSchema = (t) => Yup.object({
    email: Yup.string().email(t('validation.email')).required(t('validation.enterEmail')).matches(EMAIL_REGEX, t('validation.email')),
    password: Yup.string().required(t('validation.enterPassword'))
});

const initialFormValues = {
    email: '',
    password: '',
    remember_me: false
};

const Alerts = (t, error: string): React.ReactNode => {
    switch (error) {
        case 'user_is_not_active':
            return <AlertWithIcon severity='error'>{t.rich('auth.deactivatedAccount', {
                br: () => <br />
            })}
                   </AlertWithIcon>;
        case 'user_without_password':
            return <AlertWithIcon severity='error'>{t.rich('auth.userWithoutPassword', {
                br: () => <br />
            })}
                   </AlertWithIcon>;
        default:
            return <AlertWithIcon severity='error'>
                {t.rich('auth.loginIncorrectCredentials', {
                    br: () => <br />
                })}
                   </AlertWithIcon>;
    }
};

function LoginPage() {
    const t = useTranslations();
    const router = useRouter();
    const [showPassword, setShowPassword] = useState(false);
    const [loginError, setLoginError] = useState('');
    const [, startTransition] = useTransition();
    const { trackEvent, setUserID, identify, setOptOut } = useAmplitudeContext();

    const handleSubmit = (values, { setSubmitting }) => {
        startTransition(async () => {
            setLoginError('');

            const respError = await logIn(values.email, values.password, values.remember_me).catch(console.error);

            if (respError) {
                trackEvent(AMPLITUDE_EVENTS.SIGNIN_ERROR);
                setLoginError(respError.code);
            } else {
                const user = await getUser();

                if (user) {
                    setUserID(toAmplitudeUserID(user.id));
                    setOptOut(false);
                    trackEvent(AMPLITUDE_EVENTS.SIGN_IN, {
                        role: user?.roles[0]?.name
                    });

                    const identifyEvent = new amplitude.Identify();
                    identifyEvent.set('email', user.email);
                    identify(identifyEvent);
                }

                postLoginRedirect(router, user);
            }

            setSubmitting(false);
        });
    };

    useEffect(() => {
        document.title = formatTitle([
            t('meta.titles.login'),
            t('meta.titles.moonOne')
        ]);
    }, []);

    return (
        <PageWrapper2VerticalSections>
            <HideAbove md>
                <MoonOneLogo />
            </HideAbove>

            <StyledMainFrame>
                <HideBelow md>
                    <Title variant='uiType' size='3xl' modifier='S'>{t('auth.welcomeBack')}</Title>
                </HideBelow>
                <HideAbove md>
                    <Title variant='uiType' size='xl' modifier='S'>{t('auth.welcomeBack')}</Title>
                </HideAbove>

                <Formik
                  initialValues={initialFormValues}
                  onSubmit={handleSubmit}
                  validationSchema={validationSchema(t)}
                >
                    {({ isSubmitting }) => (
                        // noValidate drills to html <form> and prevents default validations that can trigger before custom
                        <Form noValidate method='post'>
                            <Frame>
                                <HideBelow md>
                                    <StyledSecondaryTitle>
                                        <GreyTypography variant='uiType' size='md' modifier='S'>
                                            {t('auth.loginHint')}
                                        </GreyTypography>
                                    </StyledSecondaryTitle>
                                </HideBelow>
                                <FieldsBlock>
                                    <Field
                                      component={FormikTextInput}
                                      type='email'
                                      name='email'
                                      placeholder={t('auth.emailPlaceholder')}
                                      autoComplete='email'
                                      withIncreasedHorizontalPadding
                                    />

                                    <Field
                                      component={FormikTextInput}
                                      name='password'
                                      type={showPassword ? 'text' : 'password'}
                                      autoComplete='current-password'
                                      placeholder={t('auth.passwordPlaceholder')}
                                      withIncreasedHorizontalPadding
                                      endAdornment={showPassword ? (
                                          <StyledIcon name='eye-slash-light' onClick={() => setShowPassword(false)} />
                                      ) : (
                                          <StyledIcon name='eye-light' onClick={() => setShowPassword(true)} />
                                      )}
                                    />
                                    <StyledFlexSpaceBetween>
                                        <Field
                                          component={FormikCheckbox}
                                          name='remember_me'
                                          label={t('auth.rememberMe')}
                                          inverted
                                        />
                                        <Link href='/auth/forgot-password'>
                                            <ForgotPasswordButton
                                              variant='ghost'
                                              size='xs'
                                              type='button'
                                              innerText={
                                                    <Typography
                                                      variant='uiType'
                                                      modifier='S'
                                                      size='sm'
                                                    >
                                                        {t('auth.forgotPassword')}
                                                    </Typography>
                                                }
                                            />
                                        </Link>
                                    </StyledFlexSpaceBetween>
                                </FieldsBlock>
                                <FieldsBlock>
                                    <StyledButton
                                      type='submit'
                                      variant='filled'
                                      size='sm'
                                      innerText={t('auth.signIn')}
                                      disabled={isSubmitting}
                                    />
                                    <OrBlock>
                                        <Line />
                                        {capitalize(t('common.or'))}
                                        <Line />
                                    </OrBlock>
                                    <GoogleBlock>
                                        <GoogleAuth variant='SignIn' />
                                        {loginError && Alerts(t, loginError)}
                                        <FlexCenter>
                                            <SignUpWrapper>
                                                <GreyTypography variant='uiType' size='sm' modifier='R'>{t('auth.dontHaveAnAccount')}&nbsp;</GreyTypography>
                                                <TypographyLink href='/auth/signup' variant='uiType' size='sm' modifier='S'>{t('auth.signUp')}</TypographyLink>
                                            </SignUpWrapper>
                                        </FlexCenter>
                                    </GoogleBlock>
                                </FieldsBlock>
                            </Frame>
                        </Form>
                    )}
                </Formik>
            </StyledMainFrame>

            <NeedHelpBlock />
        </PageWrapper2VerticalSections>
    );
}

export default LoginPage;
